// Adaptive Practice Coach — configuration, small data types, and the mastery gate.
// A leaf module (no imports from the engine) so coach.ts and the stores/UI can
// share it without import cycles.

import type { Dimension } from "./gate-policy";

export interface CoachConfig {
  tempoFloor: number;
  tempoStep: number;
  tempoFineStep: number;
  tempoFineBand: number;
  targetTempo: number;
  abortDensity: number;
  /** Drill speeds up when a rep's mistakes are at or below this (clean = 0 ⇒ the old
   *  strict "perfect to ramp" rule). >0 lets a near-clean rep progress off the floor
   *  instead of dead-ending; mastery (the run-through gate) stays as strict as ever. */
  rampTolerance: number;
  loopMinBars: number;
  /** Bar-scoped quick-repeat span width (bars) re-aimed at a failed bar on abort. */
  failRepeatBars: number;
  masteryReps: number;
  expandBars: number;
  // Chains. `singleChain` forces one full-piece forward passage (debug/test mode).
  singleChain: boolean;
  minChainSplitBars: number;
  chainMaxBars: number;
  overlapBars: number;
  pBackChain: number;
  pSwitchChain: number;
  /** Keep the whole piece as a final passage (end-to-end proof). */
  requireFullRun: boolean;
  /** Persistence: if the whole saved session is older than this many days, every
   *  mastered bar decays on resume (session-age, not per-bar — the pure reducer is
   *  clock-free, so per-bar last-practiced timestamps are intentionally not kept). */
  staleDays: number;
  /** Mastery reps removed from each mastered bar on a stale resume (0 = no decay). */
  decayReps: number;
  // ── Mastery gates (% over a run-through). All 100 ⇒ today's zero-mistake rule. ──
  /** Min % of expected notes that must be played. */
  masteryCompletenessPct: number;
  /** Min % of (1 − wrong ÷ expected). */
  masteryCorrectnessPct: number;
  /** "On-time" tolerance (ms); ≤ the perfect window counts only perfect hits. */
  masteryTimingMs: number;
  /** Min % of played notes that must be on-time. */
  masteryTimingPct: number;
  /** On-time pass-rate (%) required at proficiency floor (1.0). The gate ramps from
   *  here up to `masteryTimingPct` by `masteryTimingFullProf`. Equal to the ceiling ⇒
   *  today's flat rule. */
  masteryTimingFloorPct: number;
  /** Proficiency at which the on-time gate reaches `masteryTimingPct` (the ceiling). */
  masteryTimingFullProf: number;
  /** Min relative-dynamics score (%) — gates only when `dynamics` is active. */
  masteryDynamicsPct: number;
  /** Min relative-articulation score (%) — gates only when `articulation` is active. */
  masteryArticulationPct: number;
}

export const DEFAULT_COACH_CONFIG: CoachConfig = {
  tempoFloor: 0.5,
  tempoStep: 0.1,
  tempoFineStep: 0.05,
  tempoFineBand: 0.15,
  targetTempo: 1.0,
  abortDensity: 0.3,
  rampTolerance: 1,
  loopMinBars: 3,
  failRepeatBars: 1,
  masteryReps: 2,
  expandBars: 4,
  singleChain: false,
  minChainSplitBars: 4,
  chainMaxBars: 12,
  overlapBars: 2,
  pBackChain: 0.35,
  pSwitchChain: 0.5,
  requireFullRun: true,
  staleDays: 4,
  decayReps: 1,
  masteryCompletenessPct: 100,
  masteryCorrectnessPct: 100,
  masteryTimingMs: 120,
  masteryTimingPct: 100,
  masteryTimingFloorPct: 80,
  masteryTimingFullProf: 3.0,
  // Expression is fuzzy — a lenient bar ("broadly expressive"), tuned against real play.
  masteryDynamicsPct: 70,
  masteryArticulationPct: 70,
};

/** Resumable per-bar mastery (the only state worth persisting across sessions). */
export interface BarProgress {
  masteryReps: number;
  mastered: boolean;
  /** Epoch ms when this bar last earned a mastery rep. Set by the driver (a
   *  persistence timestamp, so the pure reducer stays clock-free); drives per-bar
   *  staleness decay on resume. Absent on bars never mastered / pre-C4 saves. */
  lastPracticedMs?: number;
}

/** A run-through rep's tallies over the window, for the mastery gates. */
export interface RepResult {
  /** Expected notes judged this rep (= played + missed). */
  expected: number;
  /** Expected notes the player hit. */
  played: number;
  /** Unexpected/incorrect presses. */
  wrong: number;
  /** Played notes that landed within the mastery timing tolerance. */
  onTime: number;
  /** Relative dynamics score for the rep, 0..1 (default 1 = nothing to grade). */
  dynamics?: number;
  /** Relative articulation score for the rep, 0..1 (default 1). */
  articulation?: number;
}

/**
 * Whether a run-through rep clears the mastery gates. Only the `active` dimensions are
 * checked (the Scorecard's proficiency ∪ focus subset). When `active` is omitted the
 * gate degenerates to today's fixed three (completeness/correctness/timing) — so
 * existing callers are unchanged and expression never gates by accident.
 * `timingPct` overrides the on-time pass-rate (the proficiency-ramped value); when
 * omitted the flat `c.masteryTimingPct` is used.
 */
export function repPassesGates(
  r: RepResult,
  c: CoachConfig,
  active?: ReadonlySet<Dimension>,
  timingPct?: number,
): boolean {
  if (r.expected <= 0) return r.wrong === 0;
  const on = (d: Dimension): boolean =>
    active ? active.has(d) : d === "completeness" || d === "correctness" || d === "timing";
  const completeness = (r.played / r.expected) * 100;
  const correctness = Math.max(0, 1 - r.wrong / r.expected) * 100;
  const timing = r.played > 0 ? (r.onTime / r.played) * 100 : 100;
  const dynamics = (r.dynamics ?? 1) * 100;
  const articulation = (r.articulation ?? 1) * 100;
  return (
    (!on("completeness") || completeness >= c.masteryCompletenessPct) &&
    (!on("correctness") || correctness >= c.masteryCorrectnessPct) &&
    (!on("timing") || timing >= (timingPct ?? c.masteryTimingPct)) &&
    (!on("dynamics") || dynamics >= c.masteryDynamicsPct) &&
    (!on("articulation") || articulation >= c.masteryArticulationPct)
  );
}
